"use client";

import { useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing, type AppLocale } from "@/i18n/routing";
import { resolveArticleAlternateSlug } from "@/integrations/articles/locale-switch-action";
import { cn } from "@/lib/utils/cn";

type ReplaceArgs = Parameters<ReturnType<typeof useRouter>["replace"]>;

export function LocalePicker() {
  const t = useTranslations("localePicker");
  const active = useLocale();
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const currentLocale = useLocale() as AppLocale;
  const [isPending, startTransition] = useTransition();

  function navigate(target: ReplaceArgs[0], locale: AppLocale) {
    router.replace(target, { locale });
  }

  function switchLocale(locale: AppLocale) {
    if (locale === currentLocale) return;

    // Article detail pages have per-locale slugs (matched by `translationKey`
    // frontmatter). If the article has no equivalent in the target locale we'd
    // hit a 404 — fall back to the articles index in that case.
    const isArticleDetail =
      pathname === "/articles/[slug]" || pathname === "/articles/preview/[slug]";
    const slugParam = typeof params?.slug === "string" ? params.slug : null;

    if (isArticleDetail && slugParam) {
      startTransition(async () => {
        const { slug } = await resolveArticleAlternateSlug(
          slugParam,
          currentLocale,
          locale,
        );
        if (slug) {
          navigate(
            { pathname, params: { ...params, slug } } as unknown as ReplaceArgs[0],
            locale,
          );
        } else {
          // No translation available — drop the user on the index for the
          // requested locale instead of a 404.
          navigate({ pathname: "/articles" } as unknown as ReplaceArgs[0], locale);
        }
      });
      return;
    }

    // Static + already-resolved dynamic routes: just swap locale on the same
    // canonical pathname, forwarding whatever params next/navigation reports.
    navigate({ pathname, params } as unknown as ReplaceArgs[0], locale);
  }

  return (
    <div className="inline-flex overflow-hidden rounded-full w-fit border border-current/30 p-0.5">
      {routing.locales.map((locale) => (
        <button
          key={locale}
          type="button"
          onClick={() => switchLocale(locale)}
          disabled={isPending}
          aria-pressed={active === locale}
          className={cn(
            "rounded-full px-3 py-1 font-sans text-xs uppercase tracking-wide transition-colors",
            active === locale
              ? "bg-current text-(--color-background,#f6f8fc) [&>span]:mix-blend-difference"
              : "text-inherit opacity-70 hover:opacity-100",
            isPending && "cursor-wait opacity-60",
          )}
        >
          <span>{t(locale)}</span>
        </button>
      ))}
    </div>
  );
}
